/**
 * Claimed intake: a client REMOVES a connected account or a file they added from their own
 * draft (crossmarc/steward-app#1710; founder decisions 2026-09-22: the item is fully hidden from
 * the advisor, and it is really deleted, not merely hidden).
 *
 * This module holds the values the façade route, the intake writer and the SPA must agree on:
 *
 *   • the shape of ONE removal, as the request body (`IntakeDraftRemovalSchema`), and the wider
 *     shape a `remove` queue message may carry (`IntakeDraftMessageRemovalSchema`: those two plus
 *     `accounts`, several accounts in one write, queue-only; `IntakeDraftRemoveMessageSchema` in
 *     `intakeDraft.ts`);
 *   • the draft-row attribute names that record what was removed (the TOMBSTONES), and their cap;
 *   • the one pure function that takes an account out of a snapshot, used by the writer on the
 *     row and by the SPA on its confirmed copy, so the two compute the same value and the SPA's
 *     next `snapshotConfirmed` comparison still holds.
 *
 * ## Why tombstones, and not a shorter list
 *
 * A resume link puts two devices on one draft, so a save cannot treat an omission as a removal.
 * A stale tab's autosave, or a queued save enqueued before the removal landed, could otherwise
 * put a removed item straight back. A tombstone is the explicit "this one is gone" that the
 * writer can honour without treating an omission as a removal.
 *
 * 🚨 **A tombstone names ids and a time, and NOTHING else.** No filename, no account name, no
 * institution, no balance: the point of the removal is that the client's disclosure is gone,
 * and a tombstone that remembered what it tombstoned would keep exactly the part they asked us
 * to forget. (An account tombstone written before #2309 may carry an `objectIds` array. Nothing
 * reads it now: {@link withTombstone} rebuilds every stored entry from its id and time alone, so
 * the field is dropped the next time the list is written, and a row that holds one still parses.)
 */

import { z } from 'zod';
import { ULID_PATTERN } from './intakeMessage.js';
import { INTAKE_SNAPSHOT_MAX_ACCOUNTS, IntakeSnapshotAccountIdSchema } from './intakeSnapshot.js';

/**
 * ONE removal: which kind of item, and its id. There is no `statement` target: a statement
 * object removal was retired (crossmarc/steward-app#2309), so `{target: 'statement'}` fails this
 * schema like any unknown target.
 *
 *   • `account`: `id` is the snapshot account's `accountId` (Plaid's, per-Item, opaque).
 *   • `document` (#2307): `id` is the `documentId` of a reference on the draft's `documents`. It
 *     is the one id the browser sends on a write, and it only SELECTS: the writer removes the
 *     reference only if the draft holds it, and deletes the appcore document under the user the
 *     owner binding names, so an id the draft does not hold removes nothing.
 *
 * `strictObject` and a discriminated union, so an undeclared key or a mismatched id shape is a
 * refusal rather than a silent strip.
 */
export const IntakeDraftRemovalSchema = z.discriminatedUnion('target', [
  z.strictObject({ target: z.literal('document'), id: z.string().regex(ULID_PATTERN) }),
  z.strictObject({ target: z.literal('account'), id: IntakeSnapshotAccountIdSchema }),
]);

export type IntakeDraftRemoval = z.infer<typeof IntakeDraftRemovalSchema>;

/**
 * What a queued `remove` message may carry: either of the request's two removals, or `accounts`,
 * several accounts of the draft taken off in ONE writer write. `accounts` is a queue-message
 * shape only; the HTTP route keeps {@link IntakeDraftRemovalSchema}, so a browser cannot send it.
 *
 * Why it exists (crossmarc/steward-app#2226): "Remove this bank" used to send one `account`
 * message per account, the writer processed them concurrently, and each read-modify-write failed
 * the others on the draft's version check until the retry budget ran out.
 *
 * `ids`: at least one (an empty list is refused, so a bank with no accounts sends nothing),
 * at most `INTAKE_SNAPSHOT_MAX_ACCOUNTS`, and NO duplicates (a duplicate is a caller bug and is
 * refused rather than silently collapsed).
 */
export const IntakeDraftAccountsRemovalSchema = z.strictObject({
  target: z.literal('accounts'),
  ids: z.array(IntakeSnapshotAccountIdSchema).min(1).max(INTAKE_SNAPSHOT_MAX_ACCOUNTS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate account id' }),
});

export const IntakeDraftMessageRemovalSchema = z.discriminatedUnion('target', [
  ...IntakeDraftRemovalSchema.options,
  IntakeDraftAccountsRemovalSchema,
]);

export type IntakeDraftMessageRemoval = z.infer<typeof IntakeDraftMessageRemovalSchema>;

/**
 * Every key `POST /intake/{firmSlug}/draft/removals` accepts in its JSON body. Both branches of
 * {@link IntakeDraftRemovalSchema} declare exactly these two;
 * `services/steward-app-api/src/__tests__/intakeRequestKeys.test.ts` checks it.
 */
export const INTAKE_DRAFT_REMOVAL_REQUEST_KEYS = ['target', 'id'] as const;
export type IntakeDraftRemovalRequestKey = (typeof INTAKE_DRAFT_REMOVAL_REQUEST_KEYS)[number];

/** The draft-row attribute holding account tombstones: `[{ accountId, removedAt }]`. */
export const INTAKE_DRAFT_REMOVED_ACCOUNTS_FIELD = 'removedAccounts';

/**
 * The most tombstones of ONE kind a draft keeps. When a new one would exceed it, the OLDEST is
 * dropped.
 *
 * ⚠️ **What dropping one costs, stated plainly.** A tombstone exists to beat a STALE save: a
 * queued autosave or another tab's copy, minutes old. The oldest of a hundred is long past that
 * window, so forgetting it re-opens a race nobody can still be in. Refusing the removal instead
 * would leave a client unable to remove something, which is worse.
 *
 * Why a cap at all: the count is caller-drivable. A session holder can link and remove accounts
 * repeatedly (the reader's lifetime caps below allow more account removals than this cap), and
 * each removal adds an account tombstone. A statement tombstone is now added only for a legacy
 * `statements` record on a removed account: no message can save or remove one any more
 * (crossmarc/steward-app#2365, #2309). Without a cap the row grows until appcore refuses it. Measured as
 * compact JSON (2026-09-22, when an account entry could still carry up to twenty object ids): a
 * hundred statement entries is about 8KB; a hundred account entries was about 71KB. An entry is
 * now an id and a time, so both together stay well inside DynamoDB's 400KB item limit with room
 * for the draft. (Legitimate volume, from the reader's own
 * lifetime caps as read 2026-09-22: at most 40 statement mints per draft, and at most 5 links of
 * at most 50 accounts each.)
 */
export const INTAKE_DRAFT_REMOVED_MAX = 100;

/** The ids a stored tombstone list names, read defensively (a stored row is raw attributes). */
export function tombstonedIds(stored: unknown, key: 'objectId' | 'accountId'): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(stored)) return ids;
  for (const entry of stored) {
    if (typeof entry !== 'object' || entry === null) continue;
    const id = (entry as Record<string, unknown>)[key];
    if (typeof id === 'string') ids.add(id);
  }
  return ids;
}

/** One stored tombstone entry: an id and a time. */
export type IntakeDraftTombstone = Record<string, string>;

/**
 * The tombstone list after recording `id` at `removedAt`: unchanged when `id` is already there,
 * otherwise appended and trimmed to {@link INTAKE_DRAFT_REMOVED_MAX} from the OLDEST end.
 * Entries that are not a well-formed `{ key, removedAt }` are dropped rather than carried, and
 * each is rebuilt from its known fields, so a malformed stored value cannot smuggle another
 * field (a filename) forward.
 */
export function withTombstone(
  stored: unknown, key: 'objectId' | 'accountId', id: string, removedAt: string,
): IntakeDraftTombstone[] {
  const kept: IntakeDraftTombstone[] = [];
  const build = (entryId: string, at: string): IntakeDraftTombstone => ({ [key]: entryId, removedAt: at });
  if (Array.isArray(stored)) {
    for (const entry of stored) {
      if (typeof entry !== 'object' || entry === null) continue;
      const e = entry as Record<string, unknown>;
      if (typeof e[key] === 'string' && typeof e['removedAt'] === 'string') {
        kept.push(build(e[key] as string, e['removedAt'] as string));
      }
    }
  }
  if (!kept.some((e) => e[key] === id)) kept.push(build(id, removedAt));
  return kept.slice(-INTAKE_DRAFT_REMOVED_MAX);
}

/**
 * `snapshot` with every account in `accountIds` taken out, along with the holdings that sit in
 * those accounts and any institution no remaining account names. `undefined` when nothing is
 * left (no account and no holding), so the draft carries no empty snapshot.
 *
 * 🚨 **The same function on both sides of the wire.** The writer applies it to the stored row
 * (and to a stale incoming save), the SPA to its confirmed copy after the removal is read back.
 * The SPA's later saves are confirmed by `asOf` and account count (`snapshotConfirmed`), so the
 * two must compute the same value.
 *
 * An institution survives only when a remaining account still names it. An account that names
 * none never contributed one: the reader stamps `institution` on every account of a link exactly
 * when it puts that name in `institutions`, and on none when it has no name
 * (`services/intake-reader/src/plaidSnapshot.ts`, read 2026-09-22). So a removed account's bank
 * cannot hide behind an unnamed survivor.
 *
 * Reads the value defensively and never throws: the writer's stored snapshot is raw attributes.
 */
export function snapshotWithoutAccounts<T extends Record<string, unknown>>(
  snapshot: T, accountIds: ReadonlySet<string>,
): T | undefined {
  const idOf = (entry: unknown): unknown => (
    typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>)['accountId'] : undefined
  );
  const gone = (entry: unknown): boolean => {
    const id = idOf(entry);
    return typeof id === 'string' && accountIds.has(id);
  };
  const storedAccounts = Array.isArray(snapshot['accounts']) ? snapshot['accounts'] : [];
  const storedHoldings = Array.isArray(snapshot['holdings']) ? snapshot['holdings'] : [];
  // Nothing named here: the value is returned AS IS, so a snapshot this removal does not touch
  // (including an empty or odd one) is never rewritten or dropped as a side effect.
  if (!storedAccounts.some(gone) && !storedHoldings.some(gone)) return snapshot;
  const accounts = storedAccounts.filter((a) => !gone(a));
  const holdings = storedHoldings.filter((h) => !gone(h));
  if (accounts.length === 0 && holdings.length === 0) return undefined;
  const named = new Set<string>();
  for (const account of accounts) {
    const institution = typeof account === 'object' && account !== null
      ? (account as Record<string, unknown>)['institution'] : undefined;
    if (typeof institution === 'string' && institution !== '') named.add(institution);
  }
  const institutions = (Array.isArray(snapshot['institutions']) ? snapshot['institutions'] : [])
    .filter((name) => typeof name === 'string' && named.has(name));
  return { ...snapshot, institutions, accounts, holdings };
}
